// Save shape + defaults. Persistence lives in repository.js; this file owns
// the schema (what a fresh save looks like + how to merge against defaults).

import { KingdomIds, KingdomOrder, PlotTypes, BuildingConfig } from '../config/balance.js'
import { Repository } from './repository.js'
import { key } from '../scene/hexGrid.js'

// Starter hex field: the player begins as a low-end citizen with just two owned
// tiles — their HOME and a single rice paddy beside it. Every other tile is
// UNOWNED (absent from the map = WILD bamboo) for them to claim and develop with
// Mao Coin as they build up their village. Tiles are keyed by axial "q,r".
function starterPlots() {
  return {
    [key(0, 0)]: { type: PlotTypes.HOME }, // center = home
    [key(1, 0)]: { type: PlotTypes.FARM }, // one rice paddy beside home
  }
}

function freshKingdom(id) {
  return {
    id,
    unlocked: true, // every kingdom is viewable now; gameplay is gated by profile.homeKingdom
    wealth: 0,
    clickCount: 0,
    season: 'PREP',
    seasonProgress: 0,
    cyclesCompleted: 0,
    goons: {},
    buildings: [],
    educationLevels: {},
    educatedCount: 0, // citizens that have graduated from a School (earn more)
    injuredCount: 0,  // citizens hurt fighting goons (earn nothing until healed)
    hospital: [],     // patients: [{ goonId, at }] — starving citizens admitted to the Hospital (src/game/hospital.js)
    graves: {},       // citizens lost, by reason: { starved, feral, hospital, shot, other } (headstones in the cemetery; the Registry's "lost" column)
    workMode: 'FIELDS', // FIELDS (max income, undefended) | VILLAGE (defended, less income)
    lastTaxTick: 0,
    currentEvent: null,
    seenBeats: {},
    // rosters: { [titleId]: Array<{ id, name, wealth, isPlayer }> }
    // Lazy-initialized by electionManager.init() at boot.
    rosters: {},
    // plots: { tileIndex: { type } } — missing keys = unowned (For Sale)
    plots: starterPlots(),
    // Lifetime tally of wild goons the player has slain in this kingdom
    // (touch-kills + gun-mode shots; Temple conversions don't count). Drives
    // the bounty payout and the Registry stat sheet.
    goonsSlain: 0,
    bountyTotal: 0,
    // Active raid-damage repair, when present. Shape:
    //   { mode: 'labor', endsAt: <ms>, totalDamages: <N> }
    // While set, goon income halts (laborGoonRateMult) and the damage
    // report stays visible.
    repair: null,
    // Set of tileIndex strings that are currently damaged (scorched farm
    // plots OR torched buildings). Persisted so an in-progress raid
    // survives reloads.
    damagedTiles: {},
  }
}

function freshProfile() {
  return {
    onboarded: false,
    catName: '',
    catColorId: 'CREAM',
    homeKingdom: null,
    gender: null,         // MALE / FEMALE — chosen once; decides who the Mao may marry (the server locks the first choice: docs/MARRIAGE-PLAN.md)
    citizenLoss: [],      // the last 30 citizens lost and why: { at, goonId, n, reason, left } (seasonManager.adjustGoonsOwned)
    householdReport: true, // a husband is shown his household report when he signs in (he can switch it off in the report)
    classId: null,        // ROYAL / HIGH / MIDDLE / COMMON
    title: null,          // current active title (FARMER / NOBLE / etc.)
    originalTitle: null,  // Common-class starting title — restored on resign
    licenses: {},         // { LICENSE_ID: true }
    degrees: {},          // { DEGREE_LEVEL: 'DIPLOMA_ID' }
    culturePoints: 0,
    seriesId: 'SERIES_4', // art set — Series 4 = low-poly Asian (default)
    // Citizenship start — set when onboarding completes; surfaced in the
    // Registry as "Citizen of {Kingdom} for X days".
    joinedAt: 0,
    // Optional real-world location label the player shares for the Registry
    // — city + region + country, never lat/lng (privacy). Mao Kingdom is a
    // global game so country is always part of the label when available.
    // Shape: { label: 'Boston, MA, USA', sharedAt: <ms> }
    geo: null,
    // Membership tier — gates Registry deep-view + click bonus. Real-money
    // subscription via Stripe in production; redeem code path in dev.
    membership: 'FREE',
    membershipSince: 0,
    // Player-to-player marriage. `openToMarriage` is the public flag other
    // players see in the rank list; `marriages` is the lifetime list (active
    // + divorced); `marriageProposalsIn/Out` are pending requests. Up to
    // MarriageRules.maxAsRequester active marriages as requester; an accepted
    // spouse (acceptor on any active marriage) can't request or accept more.
    openToMarriage: false,
    marriages: [],
    marriageProposalsIn: [],
    marriageProposalsOut: [],
    // Each child: { id, name, parentMarriageId, bornAt, kingdom, partner }.
    // Created via marriageManager.haveChild(); shown in Registry CHILDREN.
    children: [],
    // Achievement tracking:
    //   counters[eventName] = N (lifetime event count, across kingdoms)
    //   achievements[id] = { awardedAt }
    counters: {},
    achievements: {},
    // Active adventure, when present. Shape:
    //   { id, startedAt, endsAt, mode, discoveries: [{at, item, amount}],
    //     scribeBeatsFired: { [atSec]: true } }
    // Cleared on completion/recall. Lifetime tally lives in counters.AdventureCompleted.
    adventure: null,
    // Permanent relic / item bag of all discoveries across adventures.
    // Each entry: { id, item, amount, foundAt, biome }
    adventureBag: [],
  }
}

const defaults = () => ({
  profile: freshProfile(),
  activeKingdom: KingdomIds.WEI, // bound to profile.homeKingdom after onboarding
  kingdoms: Object.fromEntries(KingdomOrder.map((id) => [id, freshKingdom(id)])),
})

export function load() {
  const raw = Repository.load()
  if (!raw) return defaults()
  const out = defaults()
  out.activeKingdom = raw.activeKingdom ?? out.activeKingdom
  out.profile = { ...out.profile, ...(raw.profile ?? {}) }
  // The game's art set is now the low-poly Asian pack (Series 4). Migrate any
  // older voxel/line-art profile so the pack masters load + props appear.
  out.profile.seriesId = 'SERIES_4'
  for (const id of KingdomOrder) {
    out.kingdoms[id] = { ...out.kingdoms[id], ...(raw.kingdoms?.[id] ?? {}) }
    migrateToHexGrid(out.kingdoms[id])
  }
  return out
}

// Run forward migrations over a full state object. MUST be called anywhere
// state is adopted from outside the normal load() path — notably the cloud
// pull (accountManager), which would otherwise replace migrated hex plots with
// the server's old square-grid data and break tile→world lookups.
export function migrateState(s) {
  if (!s || typeof s !== 'object') return s
  if (s.profile) s.profile.seriesId = 'SERIES_4'
  for (const id of KingdomOrder) {
    if (s.kingdoms?.[id]) migrateToHexGrid(s.kingdoms[id])
  }
  return s
}

// Square→hex migration. Old saves keyed plots by numeric tile index ("78") and
// placed buildings on those indices. Hex tiles are keyed "q,r". If we detect an
// old-format plot map, reset the field to the fresh hex starter and clear the
// (now mis-placed) buildings — wealth, profile, goons, etc. all carry over.
function migrateToHexGrid(kingdom) {
  const plots = kingdom.plots ?? {}
  const plotKeys = Object.keys(plots)
  const isOldFormat = plotKeys.length > 0 && plotKeys.every((k) => !k.includes(','))
  // The pre-ownership hex model owned EVERY tile as GRASS. Detect that "all
  // owned" map (lots of GRASS, no WILD) and reset to the new claim-to-expand
  // starter so existing players get the wilderness back to buy.
  const ownedGrass = plotKeys.filter((k) => plots[k]?.type === PlotTypes.GRASS).length
  const allOwnedLegacy = ownedGrass > 30
  if (isOldFormat || plotKeys.length === 0 || allOwnedLegacy) {
    kingdom.plots = starterPlots()
    kingdom.buildings = []        // old buildings sat on now-invalid tiles
    kingdom.damagedTiles = {}
    kingdom.repair = null
  }
  pruneInvalidBuildings(kingdom)
}

// Drop buildings that violate the zoning rules for the tile they sit on, or
// that duplicate another building on the same tile. Such entries come from
// pre-zoning saves or stale dev/test placements (the old __testBuilding hook
// bypassed zoning) — left in, they'd render a misplaced diorama on, say, the
// HOME tile. Pruning here self-heals the save on every load.
function pruneInvalidBuildings(kingdom) {
  if (!Array.isArray(kingdom.buildings) || kingdom.buildings.length === 0) return
  const plots = kingdom.plots ?? {}
  const seen = new Set()
  kingdom.buildings = kingdom.buildings.filter((b) => {
    if (!b || seen.has(b.tileIndex)) return false
    const allowed = BuildingConfig[b.type]?.allowedPlotTypes
    const plotType = plots[b.tileIndex]?.type
    if (allowed && !allowed.includes(plotType)) return false
    seen.add(b.tileIndex)
    return true
  })
}

export function save(state) {
  Repository.save(state)
}

export function clear() {
  Repository.clear()
}

// Used by profileManager.defect — wipe a kingdom slice back to the fresh
// shape when the player abandons it.
export function resetKingdom(state, id) {
  state.kingdoms[id] = freshKingdom(id)
}
